import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { gosafeApi } from "../../api";
import {
  onLoadingCalendar,
  onLoading,
  onLoaded,
  onLoadCalendar,
  onFailedCalendar,
  onErrorMessage,
  onErrorSelect,
  onSuccessMessage,
  onCleanCalendar,
  onAddCalendareEvent,
  onUpdateCalendarEvent,
  onUpdatePatientSurgeryByAssessmentMade,
  onUpdatePatientSurgeryByNoPresentAssessment,
  setSelectedPatientSurgery,
  resetSelectedPatientSurgery,
  updateSelectedPatientSurgery,
} from "../../store/secretary/calendar";
import { translations, replaceEmptyToNull } from "../../helpers";

export const useCalendarStore = () => {
  const dispatch = useDispatch();

  const {
    isLoadingData,
    isLoading,
    calendar,
    message,
    selectedPatientSurgery,
  } = useSelector((state) => state.calendar);

  const starGetDoctorsByHeadquarterId = useCallback(
    async (headquarterId) => {
      try {
        dispatch(onLoading());
        dispatch(onCleanCalendar());

        const { data } = await gosafeApi.get(
          `doctor/byHeadquarterId/${headquarterId}`
        );

        dispatch(onLoaded());
        return data;
      } catch (error) {
        dispatch(
          onErrorSelect(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const starGetScheduleByDoctorId = useCallback(
    async ({ doctorId, headquarterId }) => {
      try {
        dispatch(onLoadingCalendar());

        const { data } = await gosafeApi.get(
          `scheduleEvent/byDoctorIdHeadquarterId/${doctorId}/${headquarterId}`
        );

        dispatch(onLoadCalendar(data));
      } catch (error) {
        dispatch(
          onFailedCalendar(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const startGetScheduleBySurgeryRoomId = useCallback(
    async ({ surgeryRoomId }) => {
      try {
        dispatch(onLoadingCalendar());

        const { data } = await gosafeApi.get(
          `scheduleEvent/bySurgeryRoomId/${surgeryRoomId}`
        );

        dispatch(onLoadCalendar(data));
      } catch (error) {
        dispatch(
          onFailedCalendar(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const startRegisterScheduleEvent = useCallback(
    async (body) => {
      try {
        dispatch(onLoading());

        const newBody = {
          ...replaceEmptyToNull(body),
          scheduleId: calendar.id,
          start: new Date(body.start).toISOString(),
          end: new Date(body.end).toISOString(),
        };

        const { data } = await gosafeApi.post(`scheduleEvent`, newBody);

        dispatch(onAddCalendareEvent(data.scheduleEvent));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch, calendar]
  );

  const startUpdateScheduleEventDates = useCallback(
    async (id, start, end) => {
      try {
        dispatch(onLoading());

        const newEvent = {
          start,
          end,
        };

        const { data } = await gosafeApi.put(
          `scheduleEvent/byValoration/${id}`,
          newEvent
        );

        dispatch(onUpdateCalendarEvent(data.scheduleEvent));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const startGetSurgeryRoomsByHeadquarterId = useCallback(
    async (headquarterId) => {
      try {
        dispatch(onLoading());
        dispatch(onCleanCalendar());

        const { data } = await gosafeApi.get(
          `surgeryRoom/byHeadquarterId/${headquarterId}`
        );

        dispatch(onLoaded());
        return data;
      } catch (error) {
        dispatch(
          onErrorSelect(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const startUpdateInformedConsent = useCallback(
    async ({ id, file }) => {
      try {
        dispatch(onLoading());

        const validFiles = Array.from(file).filter(
          (file) =>
            file.type === "application/pdf" || file.type.startsWith("image/")
        );

        if (validFiles.length !== file.length) {
          dispatch(onErrorMessage("Solo se permiten imágenes o PDFs"));
          return;
        }

        const formData = new FormData();
        formData.append("file", file[0]);

        const { data } = await gosafeApi.put(
          `scheduleEvent/byUploadInformedConsent/${id}`,
          formData
        );

        dispatch(onUpdateCalendarEvent(data.scheduleEvent));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error.response.data.message] ||
              error.response.data.message ||
              error.message
          )
        );
      }
    },
    [dispatch]
  );

  const startGetProtocols = useCallback(async () => {
    try {
      dispatch(onLoading());
      const { data } = await gosafeApi.get(`protocol`);
      dispatch(onLoaded());
      return data;
    } catch (error) {
      dispatch(
        onErrorSelect(
          translations[error.response.data.message] ||
            error.response.data.message
        )
      );
    }
  }, [dispatch]);

  const startUpdatePatientSurgeryByAssessmentMade = useCallback(
    async (body) => {
      try {
        dispatch(onLoading());

        // console.log("body", body);

        const { patientSurgeryId, protocolId, ...newBody } = {
          ...body,
          numberEvents: Number(body.numberEvents),
          clearanceNote: body.clearanceNote || null,
        };

        // console.log("patientSurgeryId", patientSurgeryId);
        // console.log("newBody", newBody);

        const { data } = await gosafeApi.put(
          `patientSurgery/byAssessmentMade/${patientSurgeryId}`,
          newBody
        );

        dispatch(onUpdatePatientSurgeryByAssessmentMade(data));
        dispatch(updateSelectedPatientSurgery(data.patientSurgery));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const startUpdatePatientSurgeryByNoPresentAssessment = useCallback(
    async (patientSurgeryId, scheduleEventId) => {
      try {
        dispatch(onLoading());

        const body = {
          scheduleEventId,
        };

        // console.log("patientSurgeryId", patientSurgeryId);
        // console.log("body", body);

        const { data } = await gosafeApi.put(
          `patientSurgery/byNoPresentAssessment/${patientSurgeryId}`,
          body
        );

        dispatch(onUpdatePatientSurgeryByNoPresentAssessment(data));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch]
  );

  const startUpdateByAssessment = useCallback(
    async (event) => {
      try {
        dispatch(onLoading());

        const { id, ...body } = {
          ...replaceEmptyToNull(event),
          start: new Date(event.start).toISOString(),
          end: new Date(event.end).toISOString(),
        };

        // console.log("scheduleEventId", id);
        // console.log("body", body);

        const { data } = await gosafeApi.put(
          `scheduleEvent/byAssessment/${id}`,
          body
        );

        dispatch(onUpdateCalendarEvent(data.scheduleEvent));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
        // dispatch(onErrorMessage("Hola"));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch]
  );

  const startUpdateByAssessmentDates = useCallback(
    async (id, start, end) => {
      try {
        dispatch(onLoading());

        const body = {
          start,
          end,
        };

        const { data } = await gosafeApi.put(
          `scheduleEvent/byAssessment/${id}`,
          body
        );

        dispatch(onUpdateCalendarEvent(data.scheduleEvent));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch]
  );

  const startUpdateByProcedure = useCallback(
    async (event) => {
      try {
        dispatch(onLoading());

        const { id, ...body } = {
          ...replaceEmptyToNull(event),
          start: new Date(event.start).toISOString(),
          end: new Date(event.end).toISOString(),
        };

        const { data } = await gosafeApi.put(
          `scheduleEvent/byProcedure/${id}`,
          body
        );

        dispatch(onUpdateCalendarEvent(data.scheduleEvent));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch]
  );

  const startUpdateByProcedureDates = useCallback(
    async (id, start, end) => {
      try {
        dispatch(onLoading());

        const body = {
          start,
          end,
        };

        const { data } = await gosafeApi.put(
          `scheduleEvent/byProcedure/${id}`,
          body
        );

        dispatch(onUpdateCalendarEvent(data.scheduleEvent));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch]
  );

  const startSetSelectedPatientSurgery = useCallback(
    (patientSurgery) => {
      dispatch(setSelectedPatientSurgery(patientSurgery));
    },
    [dispatch]
  );

  const startResetSelectedPatientSurgery = useCallback(() => {
    dispatch(resetSelectedPatientSurgery());
  }, [dispatch]);

  const startAttendanceProcedure = useCallback(
    async ({ attended, scheduleEventId }) => {
      try {
        dispatch(onLoading());

        const body = {
          attended,
        };

        // console.log("body", body);
        // console.log("scheduleEventId", scheduleEventId);

        const { data } = await gosafeApi.put(
          `scheduleEvent/attendanceProcedure/${scheduleEventId}`,
          body
        );

        dispatch(onUpdateCalendarEvent(data.scheduleEvent));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch]
  );

  const startAttendanceAssessment = useCallback(
    async ({ attended, scheduleEventId }) => {
      try {
        dispatch(onLoading());

        const body = {
          attended,
        };

        // console.log("body", body);
        // console.log("scheduleEventId", scheduleEventId);

        const { data } = await gosafeApi.put(
          `scheduleEvent/attendanceAssessment/${scheduleEventId}`,
          body
        );

        dispatch(onUpdateCalendarEvent(data.scheduleEvent));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch]
  );

  const startGetPatientsByClearanceStaus = useCallback(async () => {
    try {
      dispatch(onLoading());

      const { data } = await gosafeApi.get(`patientSurgery/withClearance`);

      dispatch(onLoaded());
      return data.patientSurgeries;
    } catch (error) {
      dispatch(onLoaded());
      return (
        translations[error.response.data.message] ||
        error.response.data.message ||
        error?.message
      );
    }
  }, [dispatch]);

  return {
    // Props
    isLoadingData,
    isLoading,
    calendar,
    message,
    selectedPatientSurgery,

    // Methods
    starGetDoctorsByHeadquarterId,
    starGetScheduleByDoctorId,
    startRegisterScheduleEvent,
    startUpdateScheduleEventDates,
    startGetSurgeryRoomsByHeadquarterId,
    startGetScheduleBySurgeryRoomId,
    startUpdateInformedConsent,
    startGetProtocols,
    startUpdatePatientSurgeryByAssessmentMade,
    startUpdatePatientSurgeryByNoPresentAssessment,
    startUpdateByAssessmentDates,
    startUpdateByAssessment,
    startUpdateByProcedureDates,
    startUpdateByProcedure,
    startSetSelectedPatientSurgery,
    startResetSelectedPatientSurgery,
    startAttendanceProcedure,
    startGetPatientsByClearanceStaus,
    startAttendanceAssessment,
  };
};
